import { Injectable, inject, signal, computed } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, forkJoin, of } from 'rxjs';
import { environment } from '../../environments/environment';

// ─────────────────────────────────────────────
//  Tipos e interfaces
// ─────────────────────────────────────────────

export type StepStatus = 'PENDING' | 'RUNNING' | 'SUCCESS' | 'ERROR' | 'FETCH_ERROR' | 'SKIPPED';
export type RunStatus  = 'RUNNING' | 'SUCCESS' | 'ERROR';

export interface AlloraImportError {
    itemCode: string;
    field: string;
    message: string;
}

export interface AlloraStep {
    uuid: string;         // uuid real do job (para chamadas de retry)
    key: string;
    label: string;
    group: string;
    status: StepStatus;
    statusLabel: string;  // label legível do status atual (ex: "Buscando do Protheus")
    totalProcessed: number;
    totalSuccess: number;
    totalFailures: number;
    processingTimeMs: number;
    errors: AlloraImportError[];
    message?: string; // mensagem informativa/aviso/erro do job (qualquer status)
}

export interface AlloraRun {
    id: string;
    startedAt: string;
    finishedAt: string | null;
    triggeredBy: string;
    status: RunStatus;
    steps: AlloraStep[];
}

/** Job individual retornado dentro de cada sync */
export interface AlloraSyncJob {
    uuid: string;
    jobType: string;
    /**
     * Status do job:
     * Pending    – na fila, aguardando fetch do Protheus
     * Fetching   – processando fetch, listando dados do Protheus
     * Fetched    – fetch concluído, na fila aguardando envio ao Allora
     * Processing – processando send, enviando ao Allora
     * Success    – concluído com sucesso
     * Error      – encerrado com erro
     * FetchError – itens com erro de validação ao buscar do Protheus
     */
    status: string;
    totalItems: number;
    successItems: number;
    errorItems: number;
    startedAt: string;
    finishedAt: string;
    message?: string; // presente em qualquer status (info, aviso ou erro)
}

/** Formato retornado pelo backend em GET api/AlloraSync */
export interface AlloraSyncApiItem {
    uuid: string;
    requestedBy: string;
    /**
     * Status da integração (agregado):
     * Pending | Fetching | Fetched | Processing → RUNNING
     * Success → SUCCESS   |   Error → ERROR
     */
    status: string;
    createdAt: string;
    jobs: AlloraSyncJob[];
}

/**
 * Formato retornado por GET api/AlloraSync/JobTypeConfig/Enabled (todos os usuários).
 * Retorna apenas os job types ativos.
 */
export interface AlloraJobTypeConfig {
    uuid: string;
    jobType: string | number;
    name: string;
    displayName: string;
    category: string;
    batchSize: number;
    isActive: boolean;    // normalizado internamente a partir de `enabled`
    lastSuccessAt: string | null; // ISO 8601; null ou "0001-01-01…" = nunca integrado
}

/**
 * Formato retornado por GET api/AlloraSync/JobTypeConfig (apenas Admin)
 * e pelo PUT api/AlloraSync/JobTypeConfig/{name}.
 * Usa o campo `enabled` (nome real da API).
 */
export interface AlloraJobTypeConfigAdmin {
    uuid: string;
    jobType: string | number;
    name: string;
    displayName: string;
    category: string;
    batchSize: number;
    enabled: boolean;
    lastSuccessAt: string | null;
}

/** Payload para PUT api/AlloraSync/JobTypeConfig/{uuid} */
export interface AlloraJobTypeConfigUpdatePayload {
    displayName: string;
    category: string;
    batchSize: number;
    enabled: boolean;
}

/** Item retornado por GET /api/AlloraSync/Job/{uuid}/Items */
export interface AlloraJobItem {
    externalCode: string;
    description: string;
    status: string;
    errorMessage: string;
}

// ─────────────────────────────────────────────
//  Catálogo legado de steps (usado apenas para
//  indicadores de "Última integração" na home)
// ─────────────────────────────────────────────

export interface StepDef {
    key: string;
    label: string;
    group: string;
}

export const STEP_CATALOG: StepDef[] = [
    { key: 'unidades-medida',  label: 'Unidades de Medida',      group: 'Cadastros Base' },
    { key: 'classificacoes',   label: 'Classificações de Itens', group: 'Cadastros Base' },
    { key: 'centros-custo',    label: 'Centros de Custo',        group: 'Cadastros Base' },
    { key: 'itens',            label: 'Produtos (Itens)',         group: 'Cadastros Base' },
    { key: 'centros-trabalho', label: 'Centros de Trabalho',     group: 'Cadastros Complementares' },
    { key: 'turnos',           label: 'Turnos',                  group: 'Cadastros Complementares' },
    { key: 'cargos',           label: 'Cargos e Salários',       group: 'Cadastros Complementares' },
    { key: 'equipamentos',     label: 'Equipamentos',            group: 'Cadastros Complementares' },
    { key: 'ficha-tecnica',    label: 'Ficha Técnica',           group: 'Movimentações' },
    { key: 'custos-itens',     label: 'Custos dos Itens',        group: 'Movimentações' },
];

// ─────────────────────────────────────────────
//  Helpers internos (simulação de steps)
// ─────────────────────────────────────────────

/** Mapeia o status do job da API para StepStatus + label legível */
function mapJobStatus(status: string): { stepStatus: StepStatus; statusLabel: string } {
    switch (status.toLowerCase()) {
        case 'success':    return { stepStatus: 'SUCCESS',     statusLabel: 'Sucesso' };
        case 'error':      return { stepStatus: 'ERROR',       statusLabel: 'Erro' };
        case 'fetcherror': return { stepStatus: 'FETCH_ERROR', statusLabel: 'Erro de validação' };
        case 'fetching':   return { stepStatus: 'RUNNING',     statusLabel: 'Buscando do Protheus' };
        case 'fetched':    return { stepStatus: 'RUNNING',     statusLabel: 'Aguardando envio' };
        case 'processing': return { stepStatus: 'RUNNING',     statusLabel: 'Enviando ao Allora' };
        case 'skipped':    return { stepStatus: 'SKIPPED',     statusLabel: 'Ignorado' };
        default:           return { stepStatus: 'PENDING',     statusLabel: 'Na fila' }; // 'pending'
    }
}

/**
 * Cria steps a partir dos job types selecionados pelo usuário.
 * Usado quando o usuário dispara uma nova integração (status inicial = PENDING).
 */
function buildStepsFromJobTypes(selected: AlloraJobTypeConfig[]): AlloraStep[] {
    return selected.map(jt => ({
        uuid:        '',
        key:         String(jt.jobType),
        label:       jt.displayName,
        group:       jt.category,
        status:      'PENDING' as StepStatus,
        statusLabel: 'Na fila',
        totalProcessed: 0, totalSuccess: 0, totalFailures: 0, processingTimeMs: 0,
        errors: [],
    }));
}

/**
 * Converte os jobs reais da API em AlloraStep[].
 * Usa o catálogo de job types para enriquecer label e grupo.
 */
function buildStepsFromApiJobs(jobs: AlloraSyncJob[], jobTypeConfigs: AlloraJobTypeConfig[]): AlloraStep[] {
    const isValidDate = (iso: string) => {
        const d = new Date(iso);
        return d.getFullYear() > 1000;
    };

    return jobs.map(job => {
        const config                    = jobTypeConfigs.find(jt => String(jt.jobType) === String(job.jobType));
        const { stepStatus, statusLabel } = mapJobStatus(job.status);
        const startedAt                 = new Date(job.startedAt);
        const finishedAt                = new Date(job.finishedAt);
        const processedMs               = (isValidDate(job.startedAt) && isValidDate(job.finishedAt))
            ? Math.max(0, finishedAt.getTime() - startedAt.getTime())
            : 0;

        return {
            uuid:             job.uuid,
            key:              String(job.jobType),
            label:            config?.displayName ?? job.jobType,
            group:            config?.category    ?? '',
            status:           stepStatus,
            statusLabel,
            totalProcessed:   job.totalItems,
            totalSuccess:     job.successItems ?? (stepStatus === 'SUCCESS' ? job.totalItems : 0),
            totalFailures:    job.errorItems   ?? (stepStatus === 'ERROR' || stepStatus === 'FETCH_ERROR' ? 1 : 0),
            processingTimeMs: processedMs,
            errors: [],
            message:          job.message || undefined, // mostra mensagem em qualquer status
        };
    });
}

/**
 * Gera steps mock a partir do status real da run (runs carregadas da API).
 * Usado para manter a barra de progresso na listagem sem dados detalhados.
 * O seed é derivado do UUID para resultados determinísticos.
 */
const STATUS_LABEL_MAP: Record<StepStatus, string> = {
    SUCCESS:     'Sucesso',
    ERROR:       'Erro',
    FETCH_ERROR: 'Erro de validação',
    RUNNING:     'Em execução',
    PENDING:     'Na fila',
    SKIPPED:     'Ignorado',
};

function buildMockStepsForRun(status: RunStatus, uuid: string): AlloraStep[] {
    const seed = uuid.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);

    return STEP_CATALOG.map((def, i) => {
        let stepStatus: StepStatus;

        if (status === 'SUCCESS') {
            stepStatus = 'SUCCESS';
        } else if (status === 'ERROR') {
            stepStatus = i === STEP_CATALOG.length - 1 ? 'ERROR' : 'SUCCESS';
        } else {
            stepStatus = i < Math.ceil(STEP_CATALOG.length / 2) ? 'SUCCESS' : 'PENDING';
        }

        const total    = stepStatus === 'PENDING' ? 0 : 40 + ((seed + i * 31) % 900);
        const failures = stepStatus === 'ERROR' ? 1 : 0;

        return {
            uuid:        '',
            key:         def.key,
            label:       def.label,
            group:       def.group,
            status:      stepStatus,
            statusLabel: STATUS_LABEL_MAP[stepStatus],
            totalProcessed:   total,
            totalSuccess:     total - failures,
            totalFailures:    failures,
            processingTimeMs: stepStatus === 'PENDING' ? 0 : 500 + ((seed + i * 53) % 1800),
            errors: [],
        };
    });
}

let SEQ = 1043;

// ─────────────────────────────────────────────
//  Serviço
// ─────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class AlloraIntegrationService {

    readonly #http   = inject(HttpClient);
    readonly #apiUrl = `${environment.apiUrl}/AlloraSync`;

    /** Catálogo para os cards de "última integração" na home */
    readonly stepCatalog = STEP_CATALOG;

    // ─── Estado: execução ao vivo ────────────────────────────────────────

    readonly currentRun = signal<AlloraRun | null>(null);
    readonly isRunning  = computed(() => this.currentRun() !== null);

    // ─── Estado: histórico (GET /api/AlloraSync) ─────────────────────────

    readonly history        = signal<AlloraRun[]>([]);
    readonly historyLoading = signal(false);
    readonly historyError   = signal<string | null>(null);

    // ─── Estado: job types ────────────────────────────────────────────────

    readonly jobTypes        = signal<AlloraJobTypeConfig[]>([]);
    readonly jobTypesLoading = signal(false);
    readonly jobTypesError   = signal<string | null>(null);

    // ─── Indicadores: última integração COM SUCESSO por entidade ─────────

    readonly lastSuccessByKey = computed<Record<string, string | null>>(() => {
        const map: Record<string, string | null> = {};
        const runs = this.history();
        for (const def of STEP_CATALOG) {
            const run = runs.find(r =>
                r.status === 'SUCCESS' ||
                r.steps.some(s => s.key === def.key && s.status === 'SUCCESS')
            );
            map[def.key] = run ? (run.finishedAt ?? run.startedAt) : null;
        }
        return map;
    });

    // ─── HTTP: carrega histórico + job types em paralelo ─────────────────

    loadHistory(): void {
        this.historyLoading.set(true);
        this.historyError.set(null);

        // Se job types já estão em cache, usa-os; caso contrário, busca junto.
        const configs$ = this.jobTypes().length > 0
            ? of(this.jobTypes())
            : this.#http.get<AlloraJobTypeConfig[]>(`${this.#apiUrl}/JobTypeConfig/Enabled`);

        forkJoin({
            items:   this.#http.get<AlloraSyncApiItem[]>(this.#apiUrl),
            configs: configs$,
        }).subscribe({
            next: ({ items, configs }) => {
                // Garante que os job types estão disponíveis antes de mapear os steps
                this.jobTypes.set(configs);
                this.jobTypesLoading.set(false);
                this.jobTypesError.set(null);

                const runs = [...items]
                    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
                    .map(item => this.#mapApiItemToRun(item));
                this.history.set(runs);
                this.historyLoading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar histórico Allora:', err);
                this.historyError.set('Falha ao carregar o histórico de integrações.');
                this.historyLoading.set(false);
            }
        });
    }

    // ─── HTTP: carrega tipos de job disponíveis ───────────────────────────

    loadJobTypes(): void {
        // Não recarrega se já tiver dados
        if (this.jobTypes().length > 0) return;

        this.jobTypesLoading.set(true);
        this.jobTypesError.set(null);

        this.#http.get<AlloraJobTypeConfig[]>(`${this.#apiUrl}/JobTypeConfig/Enabled`).subscribe({
            next: (types) => {
                this.jobTypes.set(types);
                this.jobTypesLoading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar job types Allora:', err);
                this.jobTypesError.set('Falha ao carregar os tipos de integração disponíveis.');
                this.jobTypesLoading.set(false);
            }
        });
    }

    reloadJobTypes(): void {
        this.jobTypes.set([]);
        this.loadJobTypes();
    }

    // ─── HTTP: carrega TODOS os job types (Admin) ─────────────────────────

    readonly allJobTypes        = signal<AlloraJobTypeConfig[]>([]);
    readonly allJobTypesLoading = signal(false);
    readonly allJobTypesError   = signal<string | null>(null);

    loadAllJobTypes(): void {
        this.allJobTypesLoading.set(true);
        this.allJobTypesError.set(null);

        this.#http.get<AlloraJobTypeConfigAdmin[]>(`${this.#apiUrl}/JobTypeConfig`).subscribe({
            next: (types) => {
                // Normaliza `enabled` → `isActive` para usar a mesma interface interna
                this.allJobTypes.set(types.map(t => ({ ...t, isActive: t.enabled })));
                this.allJobTypesLoading.set(false);
            },
            error: (err) => {
                console.error('Erro ao carregar todos os job types Allora:', err);
                this.allJobTypesError.set('Falha ao carregar as configurações.');
                this.allJobTypesLoading.set(false);
            }
        });
    }

    // ─── HTTP: cria novo sync (POST /api/AlloraSync) ──────────────────────

    createSync(jobTypes: (string | number)[]): Observable<void> {
        return this.#http.post<void>(this.#apiUrl, { jobTypes });
    }

    // ─── Dispara nova integração (simulação local) ────────────────────────

    startIntegration(user: string, selectedJobTypes: AlloraJobTypeConfig[]): void {
        if (this.isRunning() || selectedJobTypes.length === 0) return;
        const run: AlloraRun = {
            id:          `RUN-${SEQ++}`,
            startedAt:   new Date().toISOString(),
            finishedAt:  null,
            triggeredBy: user,
            status:      'RUNNING',
            steps:       buildStepsFromJobTypes(selectedJobTypes),
        };
        this.currentRun.set(run);
        this.#advance(0);
    }

    /** Reexecuta um job via POST /api/AlloraSync/Job/{uuid}/Retry */
    retryJob(jobUuid: string): Observable<void> {
        return this.#http.post<void>(`${this.#apiUrl}/Job/${jobUuid}/Retry`, {});
    }

    /** Carrega os itens com erro de um job via GET /api/AlloraSync/Job/{uuid}/Items */
    getJobItems(jobUuid: string): Observable<AlloraJobItem[]> {
        return this.#http.get<AlloraJobItem[]>(`${this.#apiUrl}/Job/${jobUuid}/Items`);
    }

    /** Atualiza configuração de um job type via PUT /api/AlloraSync/JobTypeConfig/{name} */
    updateJobTypeConfig(name: string, payload: AlloraJobTypeConfigUpdatePayload): Observable<AlloraJobTypeConfig> {
        return new Observable(observer => {
            this.#http.put<AlloraJobTypeConfigAdmin>(`${this.#apiUrl}/JobTypeConfig/${name}`, payload)
                .subscribe({
                    next: (updated) => {
                        observer.next({ ...updated, isActive: updated.enabled });
                        observer.complete();
                    },
                    error: (err) => observer.error(err),
                });
        });
    }

    // ─── Mapeamento API → modelo interno ────────────────────────────────

    #mapApiItemToRun(item: AlloraSyncApiItem): AlloraRun {
        const status = this.#mapApiStatus(item.status);
        // Usa jobs reais quando disponíveis; fallback para mock (backward compat.)
        const steps = item.jobs?.length > 0
            ? buildStepsFromApiJobs(item.jobs, this.jobTypes())
            : buildMockStepsForRun(status, item.uuid);
        return {
            id:          item.uuid,
            startedAt:   item.createdAt,
            finishedAt:  status !== 'RUNNING' ? item.createdAt : null,
            triggeredBy: item.requestedBy || '—',
            status,
            steps,
        };
    }

    #mapApiStatus(apiStatus: string): RunStatus {
        switch (apiStatus.toLowerCase()) {
            case 'success': return 'SUCCESS';
            case 'error':   return 'ERROR';
            default:        return 'RUNNING'; // 'pending' | 'running' | desconhecido
        }
    }

    // ─── Simulação interna ────────────────────────────────────────────────

    #advance(index: number): void {
        const run = this.currentRun();
        if (!run) return;

        if (index >= run.steps.length) {
            const hasError = run.steps.some(s => s.status === 'ERROR' || s.status === 'FETCH_ERROR');
            const finished: AlloraRun = {
                ...run,
                finishedAt: new Date().toISOString(),
                status: hasError ? 'ERROR' : 'SUCCESS',
            };
            this.currentRun.set(null);
            this.history.update(h => [finished, ...h]);
            return;
        }

        const step = run.steps[index];
        if (step.status === 'SKIPPED') { this.#advance(index + 1); return; }

        this.#patchCurrentStep(index, { status: 'RUNNING', statusLabel: 'Em execução' });
        const duration = 600 + Math.random() * 1200;

        setTimeout(() => {
            const total    = 40 + Math.floor(Math.random() * 900);
            const ms       = Math.round(duration);
            const hasError = Math.random() < 0.15; // 15% de chance de erro
            this.#patchCurrentStep(index, {
                status:          hasError ? 'ERROR' : 'SUCCESS',
                statusLabel:     hasError ? 'Erro' : 'Sucesso',
                totalProcessed:  total,
                totalSuccess:    hasError ? total - 1 : total,
                totalFailures:   hasError ? 1 : 0,
                processingTimeMs: ms,
                errors: [],
            });
            this.#advance(index + 1);
        }, duration);
    }

    #patchCurrentStep(index: number, patch: Partial<AlloraStep>): void {
        this.currentRun.update(run => {
            if (!run) return run;
            return { ...run, steps: run.steps.map((s, i) => i === index ? { ...s, ...patch } : s) };
        });
    }

    #patchHistoryStep(runId: string, stepKey: string, patch: Partial<AlloraStep>): void {
        this.history.update(h => h.map(r => r.id !== runId ? r : {
            ...r, steps: r.steps.map(s => s.key === stepKey ? { ...s, ...patch } : s)
        }));
    }

    #recomputeRunStatus(runId: string): void {
        this.history.update(h => h.map(r => {
            if (r.id !== runId) return r;
            const hasError = r.steps.some(s => s.status === 'ERROR' || s.status === 'FETCH_ERROR');
            return { ...r, status: hasError ? 'ERROR' : 'SUCCESS' };
        }));
    }
}
